import React, { useState, useEffect, useMemo } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from "react-simple-maps";
import { scaleQuantize } from "d3-scale";

// Use the same source as the working version
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Cache for IP geolocation data to minimize API calls
const geoCache = {};

// Predefined peer density ranges
const DENSITY_RANGES = [
  { label: "No peers", range: [0, 0] },
  { label: "1-8", range: [1, 8] },
  { label: "9-20", range: [9, 20] },
  { label: "21-50", range: [21, 50] },
  { label: "50+", range: [51, Infinity] },
];

// Fixed color array for density ranges
const DENSITY_COLORS = [
  "#2A4365", // No peers (darkest blue)
  "#3B5D8F", // 1-8 peers
  "#4C76B9", // 9-20 peers
  "#59A0C9", // 21-50 peers
  "#64D2FF", // 50+ peers (brightest turquoise)
];

const GeographicMap = ({ peerDetails, colors }) => {
  const [countryStats, setCountryStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [manualLoadTriggered, setManualLoadTriggered] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Map zoom and position state
  const [mapZoom, setMapZoom] = useState(1);
  const [mapCenter, setMapCenter] = useState([0, 0]);

  // Group peers by IP to reduce API calls
  const uniqueIps = useMemo(() => {
    if (!peerDetails || peerDetails.length === 0) return [];

    const ips = new Map();

    peerDetails.forEach((peer, index) => {
      if (!peer.ip) return;

      // Skip private IPs
      if (
        peer.ip.startsWith("10.") ||
        peer.ip.startsWith("172.16.") ||
        peer.ip.startsWith("192.168.") ||
        peer.ip === "127.0.0.1"
      ) {
        return;
      }

      if (!ips.has(peer.ip)) {
        ips.set(peer.ip, {
          ip: peer.ip,
          index,
          count: 1,
          peers: [peer],
        });
      } else {
        const entry = ips.get(peer.ip);
        entry.count += 1;
        entry.peers.push(peer);
      }
    });

    return Array.from(ips.values());
  }, [peerDetails]);

  // Manual load handler
  const handleManualLoad = () => {
    setManualLoadTriggered(true);
    setLoading(true);
    setLoadingProgress(0);
  };

  // Format first seen time nicely
  const formatFirstSeen = (timestamp) => {
    if (!timestamp) return "";

    try {
      const date = new Date(timestamp * 1000);
      if (isNaN(date.getTime())) return "";

      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return `today (${date.toLocaleTimeString()})`;
      } else if (diffDays === 1) {
        return `yesterday (${date.toLocaleTimeString()})`;
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        return date.toLocaleString();
      }
    } catch (error) {
      console.error("Error formatting first seen time:", error);
      return "";
    }
  };

  // Fetch geolocation data for all unique IPs
  useEffect(() => {
    // Only fetch data when explicitly requested
    if (!manualLoadTriggered) {
      return;
    }

    const fetchGeoData = async () => {
      if (uniqueIps.length === 0) {
        setCountryStats({});
        setLoading(false);
        setManualLoadTriggered(false);
        return;
      }

      // Start with empty country stats
      const regions = {};
      let processedCount = 0;

      try {
        // Process IPs in batches to avoid overloading
        const batchSize = 5;
        for (let i = 0; i < uniqueIps.length; i += batchSize) {
          const batch = uniqueIps.slice(i, i + batchSize);

          // Process batch in parallel
          await Promise.all(
            batch.map(async (ipData) => {
              try {
                // Check cache first
                if (geoCache[ipData.ip]) {
                  const cachedGeo = geoCache[ipData.ip];
                  updateCountryStats(
                    regions,
                    ipData,
                    cachedGeo.country,
                    cachedGeo.region,
                    cachedGeo.city,
                  );
                  processedCount++;
                  setLoadingProgress(
                    Math.round((processedCount / uniqueIps.length) * 100),
                  );
                  return;
                }

                // Use free GeoJS service (no API key required)
                const response = await fetch(
                  `https://get.geojs.io/v1/ip/geo/${ipData.ip}.json`,
                );

                if (!response.ok) {
                  throw new Error(
                    `Failed to get location for IP: ${ipData.ip}`,
                  );
                }

                const geoData = await response.json();

                // Save to cache
                geoCache[ipData.ip] = {
                  country: geoData.country,
                  region: geoData.region,
                  city: geoData.city,
                };

                // Update country stats
                updateCountryStats(
                  regions,
                  ipData,
                  geoData.country,
                  geoData.region,
                  geoData.city,
                );

                // Update progress
                processedCount++;
                setLoadingProgress(
                  Math.round((processedCount / uniqueIps.length) * 100),
                );
              } catch (err) {
                console.error(`Error fetching geo data for ${ipData.ip}:`, err);
                processedCount++;
                setLoadingProgress(
                  Math.round((processedCount / uniqueIps.length) * 100),
                );
              }
            }),
          );

          // Update state after each batch for responsive UI
          setCountryStats({ ...regions });

          // Small delay between batches to avoid rate limiting
          if (i + batchSize < uniqueIps.length) {
            await new Promise((resolve) => setTimeout(resolve, 300));
          }
        }

        // Final update of country stats
        setCountryStats({ ...regions });
      } catch (err) {
        console.error("Failed to fetch geolocation data:", err);
        setError("Failed to load geographic data");
      } finally {
        setLoading(false);
        setManualLoadTriggered(false);
      }
    };

    // Helper function to update country stats
    const updateCountryStats = (regions, ipData, country, region, city) => {
      if (!country) return;

      if (!regions[country]) {
        regions[country] = {
          peers: 0,
          reachable: 0,
          unreachable: 0,
          peerList: [],
        };
      }

      regions[country].peers += ipData.count;

      // Add peer details to country
      ipData.peers.forEach((peer) => {
        regions[country].peerList.push({
          ip: peer.ip,
          shortId: `peer-${ipData.index}-${peer.ip.split(".").pop()}`,
          reachable: peer.reachable,
          city,
          region,
          firstSeenTime: peer.firstSeenTime,
          timesReachable: peer.timesReachable,
          timesSeenInScans: peer.timesSeenInScans,
          timesUnreachable: peer.timesUnreachable,
          bucket: peer.bucket,
        });

        if (peer.reachable) {
          regions[country].reachable += 1;
        } else {
          regions[country].unreachable += 1;
        }
      });

      // Sort peer list by reachability and IP
      regions[country].peerList.sort((a, b) => {
        if (a.reachable === b.reachable) {
          return a.ip.localeCompare(b.ip);
        }
        return b.reachable - a.reachable;
      });
    };

    fetchGeoData();
  }, [manualLoadTriggered, uniqueIps]);

  // Track both hover and selected regions
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);

  // Get region data based on geo
  const getRegionData = (geo) => {
    const countryName = geo.properties.name;

    // Check for the country in our region stats
    // First try exact match
    if (countryStats[countryName]) {
      return {
        name: countryName,
        peers: countryStats[countryName].peers,
        reachable: countryStats[countryName].reachable,
        unreachable: countryStats[countryName].unreachable,
        peerList: countryStats[countryName].peerList,
      };
    }

    // Then try partial match
    const matchingCountry = Object.entries(countryStats).find(
      ([key]) => key.includes(countryName) || countryName.includes(key),
    );

    if (matchingCountry) {
      const [name, data] = matchingCountry;
      return {
        name: countryName,
        peers: data.peers,
        reachable: data.reachable,
        unreachable: data.unreachable,
        peerList: data.peerList,
      };
    } else {
      // If no match, set with 0 peers
      return {
        name: countryName,
        peers: 0,
        peerList: [],
      };
    }
  };

  const handleRegionHover = (geo) => {
    // Only set hover if not the same as selection
    if (!selectedRegion || selectedRegion.name !== geo.properties.name) {
      setHoveredRegion(getRegionData(geo));
    }
  };

  const handleRegionClick = (geo) => {
    const regionData = getRegionData(geo);

    // Toggle selection - if clicking on already selected region, clear it
    if (selectedRegion && selectedRegion.name === regionData.name) {
      setSelectedRegion(null);
    } else {
      setSelectedRegion(regionData);
      // Clear hover data when selecting to avoid duplicate info
      setHoveredRegion(null);
    }
  };

  // Handle click outside the geography (on map background)
  const handleMapClick = (evt) => {
    // Only clear selection if clicking on the map background
    if (evt.target.tagName === "svg" || evt.target.tagName === "rect") {
      setSelectedRegion(null);
    }
  };

  // Map zoom handlers
  const handleZoomIn = () => {
    setMapZoom((prevZoom) => Math.min(prevZoom * 1.5, 10));
  };

  const handleZoomOut = () => {
    setMapZoom((prevZoom) => Math.max(prevZoom / 1.5, 0.5));
  };

  const handleZoomReset = () => {
    setMapZoom(1);
    setMapCenter([0, 0]);
  };

  // Focus on US
  const handleFocusUS = () => {
    setMapZoom(3);
    setMapCenter([-95, 36]);
  };

  // Focus on Europe
  const handleFocusEurope = () => {
    setMapZoom(3);
    setMapCenter([10, 50]);
  };

  // Focus on Asia
  const handleFocusAsia = () => {
    setMapZoom(2);
    setMapCenter([100, 30]);
  };

  // Determine the last update text
  const lastUpdated =
    Object.keys(countryStats).length > 0
      ? new Date().toLocaleTimeString()
      : "Not loaded yet";

  // Function to get fill color for geography based on peer density
  const getGeographyFillColor = (geo) => {
    const countryName = geo.properties.name;

    // First try exact match
    if (countryStats[countryName]) {
      const peers = countryStats[countryName].peers;

      // Get color based on peers
      for (let i = 0; i < DENSITY_RANGES.length; i++) {
        const { range } = DENSITY_RANGES[i];
        if (peers >= range[0] && peers <= range[1]) {
          return DENSITY_COLORS[i];
        }
      }
    }

    // Then try partial match
    const matchingCountry = Object.entries(countryStats).find(
      ([key]) => key.includes(countryName) || countryName.includes(key),
    );

    if (matchingCountry) {
      const peers = matchingCountry[1].peers;

      // Get color based on peers
      for (let i = 0; i < DENSITY_RANGES.length; i++) {
        const { range } = DENSITY_RANGES[i];
        if (peers >= range[0] && peers <= range[1]) {
          return DENSITY_COLORS[i];
        }
      }
    }

    // Default to darkest blue for no peers
    return DENSITY_COLORS[0];
  };

  // Helper function to render peer list in hover/selected view
  const renderPeerList = (peers) => {
    // When selected, show all peers; when hovering, limit to 5
    const peersToDisplay = selectedRegion ? peers : peers.slice(0, 5);
    const hasMore = !selectedRegion && peers.length > 5;

    return (
      <div className="mt-2 text-xs max-h-60 overflow-y-auto pr-1">
        {peersToDisplay.map((peer, index) => (
          <div
            key={index}
            className="mb-2 border-b border-opacity-20 border-gray-500 pb-1"
          >
            <div className="flex items-center justify-between">
              <span className="font-mono">
                {peer.shortId || `peer-${index}`}
              </span>
              <span
                className={peer.reachable ? `text-green-400` : `text-red-400`}
              >
                {peer.reachable ? "Reachable" : "Unreachable"}
              </span>
            </div>
            <div className="font-mono opacity-70 text-2xs">{peer.ip}</div>
            {peer.city && (
              <div className="text-2xs opacity-70">
                {peer.city}
                {peer.region ? `, ${peer.region}` : ""}
              </div>
            )}
            {peer.bucket && (
              <div className="text-2xs opacity-70">Bucket: {peer.bucket}</div>
            )}
            {peer.firstSeenTime && (
              <div className="text-2xs opacity-70">
                First seen: {formatFirstSeen(peer.firstSeenTime)}
              </div>
            )}
            {peer.timesSeenInScans > 0 && (
              <div className="text-2xs opacity-70">
                Reliability: {peer.timesReachable}/{peer.timesSeenInScans}(
                {Math.round(
                  (peer.timesReachable / peer.timesSeenInScans) * 100,
                )}
                %)
              </div>
            )}
          </div>
        ))}
        {hasMore && (
          <div className="text-center opacity-70 my-1 py-1 border-t border-gray-500 border-opacity-20">
            + {peers.length - 5} more peers
            <div className="text-2xs mt-1">(Click country to see all)</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative" style={{ height: "500px" }}>
      {/* Controls overlay */}
      <div className="absolute top-2 right-2 z-10 flex flex-col space-y-2">
        <button
          onClick={handleManualLoad}
          style={{ backgroundColor: colors.accent }}
          className="px-3 py-1 rounded hover:bg-opacity-90 text-gray-900 flex items-center"
          disabled={loading}
        >
          {loading ? "Loading..." : "Refresh Map"}
        </button>

        <div className="flex space-x-1">
          <button
            onClick={handleZoomIn}
            style={{ backgroundColor: colors.accent }}
            className="px-3 py-1 rounded hover:bg-opacity-90 text-gray-900 flex items-center"
            title="Zoom In"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
          </button>
          <button
            onClick={handleZoomOut}
            style={{ backgroundColor: colors.accent }}
            className="px-3 py-1 rounded hover:bg-opacity-90 text-gray-900 flex items-center"
            title="Zoom Out"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18 12H6"
              />
            </svg>
          </button>
          <button
            onClick={handleZoomReset}
            style={{ backgroundColor: colors.accent }}
            className="px-3 py-1 rounded hover:bg-opacity-90 text-gray-900 flex items-center"
            title="Reset View"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>

        {/* Region focus buttons */}
        <div className="flex space-x-1">
          <button
            onClick={handleFocusUS}
            style={{ backgroundColor: colors.accent }}
            className="px-3 py-1 text-xs rounded hover:bg-opacity-90 text-gray-900"
            title="Focus on US"
          >
            US
          </button>
          <button
            onClick={handleFocusEurope}
            style={{ backgroundColor: colors.accent }}
            className="px-3 py-1 text-xs rounded hover:bg-opacity-90 text-gray-900"
            title="Focus on Europe"
          >
            EU
          </button>
          <button
            onClick={handleFocusAsia}
            style={{ backgroundColor: colors.accent }}
            className="px-3 py-1 text-xs rounded hover:bg-opacity-90 text-gray-900"
            title="Focus on Asia"
          >
            Asia
          </button>
        </div>
      </div>

      {/* Last updated info */}
      <div className="absolute top-2 left-2 z-10 bg-black bg-opacity-50 rounded px-2 py-1 text-xs">
        <span style={{ color: colors.textSecondary }}>Last updated: </span>
        <span style={{ color: colors.text }}>{lastUpdated}</span>
      </div>

      {/* Region info - shows for both hover and selected states */}
      {(hoveredRegion || selectedRegion) && (
        <div
          className="absolute left-2 z-10 bg-black bg-opacity-70 rounded px-3 py-2 text-xs"
          style={{
            top: "40px",
            maxWidth: "300px",
            maxHeight: "400px", // Increased height for better scrolling
            overflowY: "auto",
            color: colors.text,
            border: selectedRegion ? `2px solid ${colors.accent}` : "none", // Highlight selected region
          }}
        >
          <div
            className="font-bold mb-1 text-base"
            style={{ color: colors.text }}
          >
            {selectedRegion ? selectedRegion.name : hoveredRegion.name}
            {selectedRegion && (
              <button
                className="float-right text-xs opacity-70 hover:opacity-100"
                onClick={() => setSelectedRegion(null)}
                title="Close"
              >
                ✕
              </button>
            )}
          </div>

          <div className="flex space-x-4 mb-1">
            <div>
              <span style={{ color: colors.textSecondary }}>Peers: </span>
              <span style={{ color: colors.text }}>
                {selectedRegion
                  ? selectedRegion.peers || 0
                  : hoveredRegion.peers || 0}
              </span>
            </div>

            {(selectedRegion?.reachable !== undefined ||
              hoveredRegion?.reachable !== undefined) && (
              <>
                <div>
                  <span style={{ color: colors.textSecondary }}>
                    Reachable:{" "}
                  </span>
                  <span style={{ color: colors.reachable }}>
                    {selectedRegion
                      ? selectedRegion.reachable
                      : hoveredRegion.reachable}
                  </span>
                </div>
                <div>
                  <span style={{ color: colors.textSecondary }}>
                    Unreachable:{" "}
                  </span>
                  <span style={{ color: colors.unreachable }}>
                    {selectedRegion
                      ? selectedRegion.unreachable
                      : hoveredRegion.unreachable}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Show peer list for either selected or hovered region */}
          {((selectedRegion?.peerList && selectedRegion.peerList.length > 0) ||
            (hoveredRegion?.peerList && hoveredRegion.peerList.length > 0)) && (
            <>
              <div className="text-xs font-medium mt-2 border-t border-gray-500 border-opacity-40 pt-1">
                Node Details:
              </div>
              {renderPeerList(
                selectedRegion
                  ? selectedRegion.peerList
                  : hoveredRegion.peerList,
              )}
            </>
          )}
        </div>
      )}

      {/* Loading overlay with progress */}
      {loading && (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ backgroundColor: "rgba(29, 53, 87, 0.7)", zIndex: 20 }}
        >
          <div className="text-white flex flex-col items-center">
            <div className="flex items-center mb-2">
              <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Loading geographic data...
            </div>
            <div className="w-48 bg-gray-700 rounded-full h-2.5">
              <div
                className="bg-blue-400 h-2.5 rounded-full"
                style={{ width: `${loadingProgress}%` }}
              ></div>
            </div>
            <div className="mt-1 text-xs">{loadingProgress}% complete</div>
          </div>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div style={{ color: colors.error }} className="text-center p-4">
            <svg
              className="w-10 h-10 mx-auto mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="text-lg font-medium">{error}</div>
            <div
              className="mt-2 text-sm"
              style={{ color: colors.textSecondary }}
            >
              Try refreshing the page or check your connection.
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && Object.keys(countryStats).length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center p-4">
            <svg
              className="w-16 h-16 mx-auto mb-3 opacity-30"
              fill="none"
              stroke={colors.textSecondary}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="text-lg" style={{ color: colors.textSecondary }}>
              Click the "Refresh Map" button to load geographic data
            </div>
          </div>
        </div>
      )}

      <ComposableMap
        projection="geoEqualEarth"
        projectionConfig={{
          scale: 140,
          center: [0, 0],
        }}
        style={{ width: "100%", height: "100%", backgroundColor: "#1D3557" }}
        onClick={handleMapClick}
      >
        <ZoomableGroup
          center={mapCenter}
          zoom={mapZoom}
          onMoveEnd={({ coordinates, zoom }) => {
            setMapCenter(coordinates);
            setMapZoom(zoom);
          }}
        >
          {/* World map base layer with country-level coloring */}
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const fillColor = getGeographyFillColor(geo);
                const isSelected =
                  selectedRegion && selectedRegion.name === geo.properties.name;

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fillColor}
                    stroke={isSelected ? colors.accent : "#152642"}
                    strokeWidth={isSelected ? 1.5 : 0.5}
                    onClick={() => handleRegionClick(geo)}
                    onMouseEnter={() => handleRegionHover(geo)}
                    onMouseLeave={() => setHoveredRegion(null)}
                    style={{
                      default: {
                        outline: "none",
                        cursor: "pointer",
                      },
                      hover: {
                        outline: "none",
                        fill: fillColor, // Maintain color on hover
                        stroke: isSelected ? colors.accent : colors.text,
                        strokeWidth: isSelected ? 2 : 1,
                        cursor: "pointer",
                      },
                      pressed: { outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {/* Fixed map legend */}
      <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 rounded px-3 py-2 text-xs">
        <div className="mb-1 font-medium" style={{ color: colors.text }}>
          Peer Density
        </div>
        <div className="flex flex-col space-y-1">
          {DENSITY_RANGES.map((item, i) => (
            <div key={i} className="flex items-center space-x-2">
              <div
                className="w-4 h-4"
                style={{
                  backgroundColor: DENSITY_COLORS[i] || colors.bucket,
                }}
              ></div>
              <div className="text-white whitespace-nowrap">{item.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GeographicMap;
